/**
 * The process RUN surface: criteria → refresh (preview) → review the rows →
 * execute (persist). Used in two places:
 *
 *  - inside the designer (Review & Execute tab) against the unsaved working
 *    copy — layout/calc edits flow to the designer's `proc` via `onPatch` and
 *    are persisted on the designer's Save;
 *  - as the workbench "Live Preview" pane (`GenProcessLivePreviewPane`), which
 *    loads the SAVED process and runs it — layout/calc edits auto-save back to
 *    the design JSON.
 *
 * The result grid + "probe" (explain) panel + saved filters + column settings
 * are the vendored source components (see ./vendor), so this surface has full
 * functional parity with the standalone gen-process app.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import {
  AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Database, Info,
  Loader2, Play, Plus, RefreshCw, ShieldCheck, UploadCloud, X,
} from 'lucide-react'
import {
  deployProcessLogic, loadProcessObject, persistProcess, previewProcess,
  recalcProcessRow, saveProcessObject, searchLookup, validateProcess,
  type ValidationIssue,
} from '@/api/genprocess'
import { useAgentRefresh } from '@/hooks/useAgentRefresh'
import { attemptWithWarningConfirm, useWarningConfirm } from '@twasta/page-runtime/txn'
import { parseApiError } from '@/livepreview/utils'
import type {
  Calculation, GenProcess, PersistResponse, PreviewResponse, TargetColumn,
} from './types'
import { executionModeOf, sanitizeProcess } from './types'
import {
  applyMapping, importBlocker, parseCsvFile, type ImportResult,
} from './fileImport'
import { SectionHeader, cardStyle, emptyHintStyle, hintStyle, selectStyle } from './ui'
import GenprocMuiProvider from './vendor/MuiProvider'
import { setGenprocCtx } from './vendor/api'
import PreviewTable, {
  type EditableColSpec, type EditableSpec,
} from './vendor/PreviewTable'
import CalculationEditDialog from './vendor/CalculationEditDialog'
import CriteriaForm from './vendor/CriteriaForm'
import { tw } from '@twasta/page-runtime'
import {
  CustomizeBar, CustomizeMenu, PromptCustomizer, PromptPanel, humanise, processColumnSettings,
  processCriterionNames, settingsFor, truthy as czTruthy, useCustomizeSession,
  type CustomizeTransport, type ModelOverlayResult, type PromptCard,
} from '@twasta/page-runtime/customize'
import {
  modelOverlayCanCustomize, modelOverlayGet, modelOverlayPatch, modelOverlayReset,
} from '@/livepreview/api'

// The backend's HTTPException handler UNWRAPS a structured detail, so a
// genprocess error arrives as `{"error": {code, message, details}}` at the top
// level — never under `detail`. `parseApiError` reads that envelope first and
// only then the plain-string `detail` shape, so both reach the user as words
// instead of axios's "Request failed with status code NNN".
function errText(err: unknown): string {
  return parseApiError(err).message || 'Request failed'
}

interface RunPanelProps {
  /** Run view: the ⋮ "Customize" entry for the organization's functional
   *  administrator (criteria form + result grid presentation). */
  customize?: {
    transport: CustomizeTransport; enabled: boolean
    /** Every saved change: the process as this organization now sees it. */
    onEffective: (res: ModelOverlayResult) => void
  }
  project: string
  process: GenProcess
  /** Initial criteria values (designer passes its live ones). */
  criteriaValues: Record<string, unknown>
  onCriteriaChange: (next: Record<string, unknown>) => void
  /** Persist layout (widths/headings/filters) + calc edits back to the design.
      Designer passes `patch` (saved on Save); the live-preview pane passes an
      auto-saving updater. Omitted → grid edits stay session-only and the calc
      edit/move/delete affordances are hidden. */
  onPatch?: (p: Partial<GenProcess>) => void
  /** Lift the columns produced by a preview run up to the designer, so tabs
      that unmount RunPanel (e.g. Target) can still offer them as source
      columns. Called after every successful preview. */
  onPreviewColumns?: (cols: string[]) => void
  /** Run the SAVED process by name instead of sending `process` inline. The
      Run view (project users, guest tenants, the downloaded runtime) must:
      the server runs an inline definition only for a designer, because it is
      SQL the caller wrote. `process` is then used for display only. */
  savedObjName?: string
}

export default function RunPanel({
  project, process, criteriaValues, onCriteriaChange, onPatch, onPreviewColumns,
  savedObjName, customize,
}: RunPanelProps) {
  // Ambient project context for the vendored dialogs' project-less API calls
  // (e.g. CalculationEditDialog → LookupsApi.list()).
  setGenprocCtx({ project })

  // ── Customize mode (⋮ in the Result toolbar) ───────────────────────────
  // The organization's functional administrator arranges the criteria form
  // and the result grid; Refresh / Execute are off while it is on.
  const [customizing, setCustomizing] = useState(false)
  const [czView, setCzView] = useState<'criteria' | 'grid'>('criteria')
  const [czPanel, setCzPanel] = useState<{ kind: 'crit' | 'col'; key: string; anchor: HTMLElement } | null>(null)
  const czNoTransport = useMemo<CustomizeTransport>(() => ({
    get: () => Promise.reject(new Error('Customize is not available here')),
    patch: () => Promise.reject(new Error('Customize is not available here')),
    reset: () => Promise.reject(new Error('Customize is not available here')),
  }), [])
  const czSession = useCustomizeSession({
    objType: 'G', objName: savedObjName || '', transport: customize?.transport ?? czNoTransport,
    active: customizing, onEffective: (r) => customize?.onEffective(r),
  })

  const [preview, setPreview] = useState<PreviewResponse | null>(null)
  const [previousPreview, setPreviousPreview] = useState<PreviewResponse | null>(null)
  const [running, setRunning] = useState<'preview' | 'persist' | 'validate' | 'deploy' | null>(null)
  const [limit, setLimit] = useState(50)
  const [persistResult, setPersistResult] = useState<PersistResponse | null>(null)
  const [selectedRows, setSelectedRows] = useState<Record<string, unknown>[]>([])
  const [editedRows, setEditedRows] = useState<Record<string, unknown>[] | null>(null)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  // Validation report (from Execute's 422 or the Validate button) + the cell
  // the user clicked to trace, which the grid scrolls to and flashes.
  const [report, setReport] = useState<{
    code: string; errors: ValidationIssue[]; warnings: ValidationIssue[]
    prompts: ValidationIssue[]
    /** Transaction mode: groups already committed before one was refused. */
    savedGroups?: number
  } | null>(null)
  const [focusCell, setFocusCell] = useState<{ row: number; col: string; n: number } | null>(null)
  // The transaction save's own "ignore warning(s) and proceed" dialog, reused
  // verbatim so a process warns exactly the way a transaction does.
  const { confirmWarnings, warningsModal } = useWarningConfirm()
  // Criteria panel collapse — auto-collapses once data is retrieved, re-expandable.
  const [criteriaCollapsed, setCriteriaCollapsed] = useState(false)
  // Friendly labels for each (lower-cased) criterion name, lifted from the
  // CriteriaForm parse so the collapsed recap shows "Location Code" not the
  // raw `location_code` technical name.
  const [criteriaLabels, setCriteriaLabels] = useState<Record<string, string>>({})

  const canEditCalcs = !!onPatch
  // What each run call names: the saved object (Run view) or the working copy.
  const runTarget = useCallback(
    (against: GenProcess = process): { objName?: string; process?: GenProcess } =>
      savedObjName ? { objName: savedObjName } : { process: against },
    [savedObjName, process])

  const params = useMemo(() => {
    const o: Record<string, unknown> = {}
    Object.entries(criteriaValues).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== ''
          && !(Array.isArray(v) && v.every((x) => x === ''))) o[k] = v
    })
    return o
  }, [criteriaValues])

  // One-line recap of the supplied criteria, shown on the collapsed header.
  const criteriaSummary = useMemo(() => {
    const parts = Object.entries(params).map(([k, v]) => {
      const label = criteriaLabels[k.toLowerCase()] || k
      const val = Array.isArray(v) ? v.filter((x) => x !== '' && x != null).join('–') : String(v)
      return `${label}=${val}`
    })
    const s = parts.join('  ·  ')
    return s.length > 140 ? `${s.slice(0, 137)}…` : s
  }, [params, criteriaLabels])

  // Editable-column spec for the grid: 'all' or a {col -> {type,lookup}} map.
  // Built from the editableColumns LIST (so it works without target_columns —
  // e.g. transaction persist mode), resolving each column's type + lookup from
  // target_columns first, then the mode-independent `editable_lookups` map.
  const editableSpec = useMemo<EditableSpec | undefined>(() => {
    if (!process.columnEditing) return undefined
    if (process.editableColumns === 'all' || !process.editableColumns) return 'all'
    const list = Array.isArray(process.editableColumns) ? process.editableColumns : []
    const tcBySrc = new Map<string, TargetColumn>()
    for (const c of process.target_columns || []) {
      tcBySrc.set((c.source?.trim() || c.name).toLowerCase(), c)
    }
    const elk = process.editable_lookups || {}
    const m: Record<string, EditableColSpec> = {}
    for (const raw of list) {
      const col = raw.toLowerCase()
      const tc = tcBySrc.get(col)
      m[col] = { type: tc?.type || 'string', lookup: tc?.lookup ?? elk[col] }
    }
    return m
  }, [process.columnEditing, process.editableColumns, process.target_columns,
    process.editable_lookups])

  // Per-organization grid presentation (screen customization): hidden columns
  // drop out of the grid (still carried in the rows), `order` moves a column,
  // `protect` makes an otherwise editable column read-only.
  const colSettings = useMemo(() => processColumnSettings(process), [process])
  const lockedColumns = useMemo(
    () => new Set(Object.entries(colSettings).filter(([, s]) => s.protect).map(([c]) => c)),
    [colSettings])
  const shownColumns = useMemo<string[]>(() => {
    const cols = preview?.columns || []
    if (!Object.keys(colSettings).length) return cols
    return cols
      .map((c, i) => ({ c, i, s: colSettings[c.toLowerCase()] }))
      .filter((x) => !x.s?.hidden)
      .sort((a, b) => ((a.s?.order ?? (a.i + 1) * 10) - (b.s?.order ?? (b.i + 1) * 10)) || (a.i - b.i))
      .map((x) => x.c)
  }, [preview, colSettings])

  // Merge calc headers into the source-column header overrides for the grid.
  const columnHeaders = useMemo(() => {
    const m: Record<string, string> = { ...(process.column_headers || {}) }
    for (const c of process.calculations || []) {
      if (c.header?.trim()) m[c.col_name] = c.header.trim()
    }
    return m
  }, [process.column_headers, process.calculations])

  // Live column list for the calc editor (preview columns, else target sources).
  const availableColumns = useMemo<string[]>(() => {
    if (preview?.columns?.length) return preview.columns
    return [...new Set(
      (process.target_columns || []).map((c) => c.source || c.name).filter(Boolean))]
  }, [preview, process.target_columns])

  // ── imported-file source ────────────────────────────────────────────────
  // A file process has no criteria to prompt for; the file IS the input. The
  // parsed rows are held here and sent with every preview/validate/execute, so
  // the whole downstream surface (calcs, grid edits, validations, persist)
  // behaves exactly as it does for a SQL source.
  const fromFile = (process.source_kind || 'sql') === 'file'
  const fileInput = useRef<HTMLInputElement | null>(null)
  const [imported, setImported] = useState<ImportResult | null>(null)
  const [importError, setImportError] = useState('')
  const importBlocked = importBlocker(imported)
  const inputRows = (fromFile && imported && !importBlocked) ? imported.rows : undefined

  const pickImportFile = useCallback(async (file: File | undefined) => {
    if (!file) return
    setImportError('')
    try {
      const parsed = await parseCsvFile(file)
      const result = applyMapping(parsed, process.file_source)
      setImported(result)
      setPreview(null)
      setPersistResult(null)
      setReport(null)
      if (result.warning) toast(result.warning, { icon: '⚠️' })
    } catch (e) {
      setImported(null)
      setImportError(e instanceof Error ? e.message : String(e))
    } finally {
      if (fileInput.current) fileInput.current.value = ''
    }
  }, [process.file_source])

  const runPreview = useCallback(async (against: GenProcess = process) => {
    setRunning('preview')
    setPersistResult(null)
    setPreviousPreview(preview)
    try {
      const result = await previewProcess({
        project, ...runTarget(against), params, inputRows,
        limit: limit > 0 ? limit : undefined, withTraces: true,
      })
      setPreview(result)
      if (result?.columns?.length) onPreviewColumns?.(result.columns)
      setSelectedRows([])
      setEditedRows(null)
      setCriteriaCollapsed(true)   // data retrieved → fold the criteria away
    } catch (err) {
      toast.error(errText(err))
    } finally {
      setRunning(null)
    }
  }, [project, process, params, limit, preview, onPreviewColumns, inputRows, runTarget])

  // Which rows Execute / Validate act on. `undefined` rows ⇒ the backend
  // re-runs the source SQL and writes everything.
  const resolveRows = useCallback(():
    { rows?: Record<string, unknown>[]; what: string } | { error: string } => {
    const isTxn = process.persist_mode === 'transaction'
    const blMethod = (process.business_logic as
      { mode?: string; business_logic?: { method?: string } } | undefined)
    const dest = process.persist_mode === 'business_logic'
      ? `run the business logic "${blMethod?.business_logic?.method || '?'}"`
      : isTxn
        ? `create transactions of "${process.transaction_target?.obj_name || '?'}"`
        : `write rows into "${process.target_table || '?'}"`
    if (process.MultiRowSelect) {
      if (!selectedRows.length) return { error: 'Select at least one row on the result grid first.' }
      return { rows: selectedRows, what: `${dest} from ${selectedRows.length} selected row(s)` }
    }
    if (process.columnEditing && preview) {
      const rows = editedRows ?? preview.rows
      return { rows, what: `${dest} from the ${rows.length} previewed row(s) (edits applied)` }
    }
    return { what: `re-run the process and ${dest}` }
  }, [process, selectedRows, editedRows, preview])

  // Warnings are acknowledged the same way a transaction save acknowledges
  // them: the shared modal lists them and "proceed" retries with their codes.
  // The last 422's issues are kept so the modal can say WHICH row each warning
  // is about — the shared WarningItem carries only code/field/message.
  const lastWarnings = useRef<ValidationIssue[]>([])

  const runPersist = useCallback(async () => {
    const r = resolveRows()
    if ('error' in r) { toast.error(r.error); return }
    // One confirmation for the whole run, before the first attempt — a retry
    // after acknowledging warnings must not ask again.
    if (!confirm(`Execute will ${r.what}. Continue?`)) return
    setRunning('persist')
    try {
      const result = await attemptWithWarningConfirm(
        async (ignoreWarnings) => {
          try {
            return await persistProcess({
              project, ...runTarget(), params, rows: r.rows,
              ignoreWarnings: ignoreWarnings.length ? ignoreWarnings : undefined,
              inputRows,
            })
          } catch (err) {
            const { code, details } = parseApiError(err)
            if (code === 'VALIDATION_WARNINGS') {
              lastWarnings.current =
                ((details || {}) as { warnings?: ValidationIssue[] }).warnings || []
            }
            throw err
          }
        },
        (warnings) => confirmWarnings(warnings.map((w, i) => ({
          ...w, message: issueLabel(lastWarnings.current[i], w.message),
        }))),
      )
      setReport(null); setFocusCell(null); setPersistResult(result)
      // What the business logic itself reported wins as the headline — it knows
      // what the run actually did; the counts below are the generic fallback.
      if (result.message) {
        toast.success(result.message)
      } else if (result.mode === 'business_logic') {
        toast.success(`Processed ${result.rows ?? 0} row(s)`)
      } else if (result.mode === 'transaction') {
        toast.success(`Created ${result.transactions ?? 0} transaction(s) from ${result.rows ?? 0} row(s)`)
      } else if (result.mode === 'merge' || result.mode === 'replace') {
        // Analytics targets report one affected count, not an insert/update
        // split — say what actually happened rather than implying a breakdown.
        const verb = result.mode === 'merge' ? 'Merged' : 'Replaced'
        toast.success(
          `${verb} ${result.written ?? 0} row(s) into ${result.target_table}`
          + (result.target_format ? ` (${result.target_format})` : ''))
      } else {
        toast.success(
          `Inserted ${result.inserted ?? 0}`
          + ((result.updated ?? 0) > 0 ? `, updated ${result.updated}` : '')
          + ` row(s) into ${result.target_table}`)
      }
      if (result.warning) toast(result.warning, { icon: '⚠️', duration: 6000 })
      const prompts = (result as { prompts?: ValidationIssue[] }).prompts
      if (Array.isArray(prompts) && prompts.length) {
        setReport({ code: 'OK', errors: [], warnings: [], prompts })
      }
    } catch (err) {
      const { code, details } = parseApiError(err)
      if (code === 'VALIDATION_FAILED' || code === 'VALIDATION_WARNINGS') {
        // Errors — and warnings the user declined — land in the report panel,
        // where each issue is clickable and scrolls the grid to its cell so the
        // data can be corrected and Execute pressed again.
        const d = (details || {}) as {
          errors?: ValidationIssue[]
          warnings?: ValidationIssue[]
          prompts?: ValidationIssue[]
          saved_groups?: number
        }
        setPersistResult(null)
        setReport({
          code,
          errors: d.errors || [],
          warnings: d.warnings || [],
          prompts: d.prompts || [],
          savedGroups: d.saved_groups,
        })
      } else {
        toast.error(errText(err))
      }
    } finally {
      setRunning(null)
    }
  }, [resolveRows, project, params, inputRows, confirmWarnings, runTarget])

  const runValidate = useCallback(async () => {
    const r = resolveRows()
    if ('error' in r) { toast.error(r.error); return }
    setRunning('validate')
    try {
      const rep = await validateProcess({
        project, ...runTarget(), params, rows: r.rows, inputRows })
      setReport({ code: rep.ok ? 'OK' : 'VALIDATION_FAILED', errors: rep.errors, warnings: rep.warnings, prompts: rep.prompts })
      if (rep.ok && !rep.warnings.length) toast.success(`All ${rep.row_count} row(s) valid`)
    } catch (err) {
      toast.error(errText(err))
    } finally {
      setRunning(null)
    }
  }, [resolveRows, project, params, inputRows, runTarget])

  const deployLogic = useCallback(async () => {
    setRunning('deploy')
    try {
      const { results } = await deployProcessLogic(project, process.obj_name || '')
      const bad = results.filter((x) => x.status && x.status !== 'ok' && x.status !== 'empty')
      if (!results.length) toast('No business-logic functions found to deploy.', { icon: 'ℹ️' })
      else if (bad.length) toast.error(`${bad.length} function(s) failed: ${bad.map((b) => b.name || '?').join(', ')}`)
      else toast.success(`Deployed ${results.length} business-logic function(s)`)
    } catch (err) {
      toast.error(errText(err))
    } finally {
      setRunning(null)
    }
  }, [project, process.obj_name])

  const recalc = useCallback(async (
    row: Record<string, unknown>, editedColumn: string,
  ) => {
    const res = await recalcProcessRow({ project, ...runTarget(), params, row, editedColumn })
    const out: Record<string, unknown> = { ...res.row }
    // Smuggle item_change protect flags to the grid (stripped before display).
    if (res.protect && Object.keys(res.protect).length) {
      ;(out as Record<string, unknown>).__protect = res.protect
    }
    if (res.warning) toast(String(res.warning), { icon: '⚠️' })
    return out
  }, [project, params, runTarget])

  // Item_change / validation business-logic present → offer "Deploy logic".
  const hasBL = useMemo(() => {
    const ics = Object.values(process.item_changes || {})
    const icBL = ics.some((ic) => (ic as { mode?: string })?.mode === 'business_logic')
    const vBL = (process.validations || []).some(
      (v) => (v as { type?: string })?.type === 'business_logic')
    const procBL = (process.business_logic as { mode?: string } | undefined)?.mode
      === 'business_logic'
    return icBL || vBL || procBL
  }, [process.item_changes, process.validations, process.business_logic])

  // Cells (rowIndex:col) flagged by the current validation report.
  const errorCells = useMemo(() => {
    const s = new Set<string>()
    if (report) {
      for (const i of [...report.errors, ...report.warnings]) {
        // A whole-run issue (process business logic reporting about the set,
        // not a row) has no cell to flag.
        if (i.row == null) continue
        s.add(`${i.row}:${(i.column || '').toLowerCase()}`)
      }
    }
    return s
  }, [report])

  const fetchLookup = useCallback(async (
    column: string, search: string, row: Record<string, unknown>,
  ) => {
    const { rows } = await searchLookup({ project, ...runTarget(), column, search, row })
    return rows
  }, [project, runTarget])

  const fetchCriterionLookup = useCallback(async (
    criterion: string, search: string,
  ) => {
    const { rows } = await searchLookup({
      project, ...runTarget(), criterion, search, row: criteriaValues,
    })
    return rows
  }, [project, runTarget, criteriaValues])

  // ----- calc edit / move / delete (only when edits can persist) -----------
  const openEditor = (index: number | null) => {
    setEditingIndex(index)
    setEditorOpen(true)
  }

  const onSaveCalc = useCallback(async (
    next: Calculation,
  ): Promise<{ ok: boolean; error?: string }> => {
    const calcs = (process.calculations || []).slice()
    if (editingIndex == null) calcs.push(next)
    else calcs[editingIndex] = next
    const tentative: GenProcess = { ...process, calculations: calcs }
    setRunning('preview')
    setPreviousPreview(preview)
    try {
      const result = await previewProcess({
        project, process: tentative, params,
        limit: limit > 0 ? limit : undefined, withTraces: true,
      })
      onPatch?.({ calculations: calcs })
      setPreview(result)
      setEditorOpen(false)
      setEditingIndex(null)
      return { ok: true }
    } catch (e) {
      return { ok: false, error: errText(e) }
    } finally {
      setRunning(null)
    }
  }, [process, editingIndex, preview, project, params, limit, onPatch])

  const onDeleteCalc = useCallback(async (index: number) => {
    const c = (process.calculations || [])[index]
    if (!c) return
    if (!confirm(`Delete column "${c.col_name}"? This removes the calculation.`)) return
    const calcs = (process.calculations || []).slice()
    calcs.splice(index, 1)
    onPatch?.({ calculations: calcs })
    await runPreview({ ...process, calculations: calcs })
  }, [process, onPatch, runPreview])

  const onMoveCalc = useCallback(async (index: number, delta: number) => {
    const calcs = (process.calculations || []).slice()
    const j = index + delta
    if (j < 0 || j >= calcs.length) return
    ;[calcs[index], calcs[j]] = [calcs[j], calcs[index]]
    onPatch?.({ calculations: calcs })
    await runPreview({ ...process, calculations: calcs })
  }, [process, onPatch, runPreview])

  const calcIndexByColumn = useCallback((col: string) =>
    (process.calculations || []).findIndex(
      (c) => c.col_name.toLowerCase() === col.toLowerCase()),
  [process.calculations])

  const isTxnMode = process.persist_mode === 'transaction'
  const isBlMode = process.persist_mode === 'business_logic'
  // A PROVIDER (persist_mode "none") only previews — its rows feed another
  // form's lines — so it has no Execute at all (the server refuses one too).
  const isProvider = process.persist_mode === 'none'
  const execMode = executionModeOf(process)
  const hasRows = !!preview && preview.rows.length > 0
  // Row-selection AND edit modes keep Execute hidden until the rows have been
  // retrieved AND at least one is selected (editing a cell auto-selects its
  // row), so the user can't fire it on an empty grid / with nothing chosen.
  const needsSelection = execMode === 'select' || execMode === 'edit'
  // A file process cannot run until a usable file is imported — there is no
  // query to fall back on.
  const awaitingImport = fromFile && (!imported || !!importBlocked)
  const showExecute = awaitingImport
    ? false
    : needsSelection ? (hasRows && selectedRows.length > 0) : true

  // ── Customize mode content ─────────────────────────────────────────────
  const czCritNames = processCriterionNames(process)
  const czCritPath = (name: string, leaf: string) => `criteria_settings.${name}.${leaf}`
  const czCritDefault = (name: string): string => {
    const s0 = settingsFor(process.criteria_settings, name)
    if (s0.default != null && String(s0.default).trim() !== '') return String(s0.default)
    const raw = Object.entries(process.default_params || {}).find(([k]) => k.toLowerCase() === name.toLowerCase())?.[1]
    return raw != null ? String(raw) : ''
  }
  const czCritCards: PromptCard[] = czCritNames
    .map((name, i) => ({ name, i, s0: settingsFor(process.criteria_settings, name) }))
    .sort((a, b) => ((Number(a.s0.order) || (a.i + 1) * 10) - (Number(b.s0.order) || (b.i + 1) * 10)) || (a.i - b.i))
    .map(({ name, s0 }) => ({
      key: name,
      label: String(s0.label || criteriaLabels[name.toLowerCase()] || humanise(name)),
      valueText: czCritDefault(name),
      hidden: czTruthy(s0.hidden),
      protect: czTruthy(s0.protect),
      customized: czSession.countCustomized(`criteria_settings.${name}`) > 0,
    }))
  const czCriteria = (
    <div style={{ marginTop: 8 }}>
      {czCritCards.length === 0
        ? <div style={{ ...hintStyle, marginTop: 0 }}>This process has no criteria prompts.</div>
        : <PromptCustomizer items={czCritCards}
            openKey={czPanel?.kind === 'crit' ? czPanel.key : null}
            onOpen={(key, anchor) => setCzPanel({ kind: 'crit', key, anchor })}
            onShow={(key) => void czSession.apply({ set: [{ path: czCritPath(key, 'hidden'), value: false }], reset: [] }, 'Show prompt')}
            onReorder={(keys) => void czSession.apply({
              set: keys.map((k, i) => ({ path: czCritPath(k, 'order'), value: (i + 1) * 10 })), reset: [],
            }, 'Reorder prompts')} />}
    </div>
  )
  const czCalcNames = new Set((process.calculations || []).map((c) => String(c.col_name || '').toLowerCase()))
  const czColNames: string[] = (() => {
    const out: string[] = []
    const add = (c: string) => { const t = String(c || '').trim(); if (t && !out.some((o) => o.toLowerCase() === t.toLowerCase())) out.push(t) }
    ;(preview?.columns || []).forEach(add)
    ;(process.calculations || []).forEach((c) => add(c.col_name))
    Object.keys(process.column_headers || {}).forEach(add)
    Object.keys(process.column_settings || {}).forEach(add)
    return out
  })()
  const czColHeadPath = (col: string) => {
    const calc = (process.calculations || []).find((c) => String(c.col_name).toLowerCase() === col.toLowerCase())
    if (calc) return `calculations.@items.${calc.col_name}.header`
    const hk = Object.keys(process.column_headers || {}).find((k) => k.toLowerCase() === col.toLowerCase())
    return `column_headers.${hk ?? col}`
  }
  const czColCards: PromptCard[] = czColNames
    .map((col, i) => ({ col, i, s0: settingsFor(process.column_settings, col) }))
    .sort((a, b) => ((Number(a.s0.order) || (a.i + 1) * 10) - (Number(b.s0.order) || (b.i + 1) * 10)) || (a.i - b.i))
    .map(({ col, s0 }) => ({
      key: col,
      label: Object.entries(columnHeaders).find(([k]) => k.toLowerCase() === col.toLowerCase())?.[1] || col,
      valueText: czCalcNames.has(col.toLowerCase()) ? 'calculated' : '',
      hidden: czTruthy(s0.hidden),
      protect: czTruthy(s0.protect),
      customized: czSession.countCustomized(`column_settings.${col}`) > 0 || czSession.isCustomized(czColHeadPath(col)),
    }))
  const czGrid = czColCards.length === 0 ? (
    <div style={{ ...hintStyle, marginTop: 0 }}>
      Refresh the data once so the result grid's columns are known.
      <button className="btn btn-ghost btn-sm" style={{ marginLeft: 8 }} onClick={() => void runPreview()}>↻ Refresh data</button>
    </div>
  ) : (
    <PromptCustomizer items={czColCards} noun="column"
      trayTitle="Hidden columns" trayHint="Hidden columns still run and are written — they are only not shown."
      openKey={czPanel?.kind === 'col' ? czPanel.key : null}
      onOpen={(key, anchor) => setCzPanel({ kind: 'col', key, anchor })}
      onShow={(key) => void czSession.apply({ set: [{ path: `column_settings.${key}.hidden`, value: false }], reset: [] }, 'Show column')}
      onReorder={(keys) => void czSession.apply({
        set: keys.map((k, i) => ({ path: `column_settings.${k}.order`, value: (i + 1) * 10 })), reset: [],
      }, 'Reorder columns')} />
  )
  const czEditableList = process.editableColumns === 'all' || !process.editableColumns
    ? null : (process.editableColumns as string[]).map((c) => c.toLowerCase())
  const czPanelNode = customizing && czPanel ? (() => {
    const apply = (path: string, value: unknown, label: string) =>
      void czSession.apply({ set: [{ path, value }], reset: [] }, label)
    const reset = (path: string, label: string) => void czSession.apply({ set: [], reset: [path] }, label)
    if (czPanel.kind === 'crit') {
      const name = czPanel.key
      const s0 = settingsFor(process.criteria_settings, name)
      const coreS = settingsFor((czSession.core as any)?.criteria_settings, name)
      return (
        <PromptPanel anchor={czPanel.anchor} onClose={() => setCzPanel(null)}
          title={String(s0.label || criteriaLabels[name.toLowerCase()] || humanise(name))}
          sub={`?.${name} · criteria prompt`} saved={czSession.status === 'saved'} needsDefaultToFix
          label={{
            value: String(s0.label || criteriaLabels[name.toLowerCase()] || humanise(name)),
            standard: String(coreS.label || humanise(name)),
            customized: czSession.isCustomized(czCritPath(name, 'label')),
            onReset: () => reset(czCritPath(name, 'label'), 'Label'),
            onCommit: (v) => v.trim() ? apply(czCritPath(name, 'label'), v.trim(), 'Label') : reset(czCritPath(name, 'label'), 'Label'),
          }}
          show={{ on: !czTruthy(s0.hidden), onChange: (on) => apply(czCritPath(name, 'hidden'), !on, on ? 'Show prompt' : 'Hide prompt'), hint: 'Hidden prompts run on their default' }}
          editable={{ on: !czTruthy(s0.protect), onChange: (on) => apply(czCritPath(name, 'protect'), !on, on ? 'Editable' : 'Read-only'), hint: 'Off fixes it for your organization' }}
          defaultValue={{
            value: czCritDefault(name),
            customized: czSession.isCustomized(czCritPath(name, 'default')),
            placeholder: 'Leave empty to make people choose',
            onReset: () => reset(czCritPath(name, 'default'), 'Default'),
            onCommit: (v) => v.trim() ? apply(czCritPath(name, 'default'), v, 'Default') : reset(czCritPath(name, 'default'), 'Default'),
          }}
          onResetAll={() => void czSession.resetUnder([`criteria_settings.${name}`], 'Reset prompt')}
          resetAllDisabled={!czSession.countCustomized(`criteria_settings.${name}`)}
        />
      )
    }
    const col = czPanel.key
    const s0 = settingsFor(process.column_settings, col)
    const editableCapable = czEditableList === null || czEditableList.includes(col.toLowerCase())
    const head = Object.entries(columnHeaders).find(([k]) => k.toLowerCase() === col.toLowerCase())?.[1] || col
    return (
      <PromptPanel anchor={czPanel.anchor} onClose={() => setCzPanel(null)}
        title={head} sub={`${col} · result grid column${czCalcNames.has(col.toLowerCase()) ? ' · calculated' : ''}`}
        saved={czSession.status === 'saved'}
        label={{
          caption: 'Column heading', value: head,
          customized: czSession.isCustomized(czColHeadPath(col)),
          onReset: () => reset(czColHeadPath(col), 'Heading'),
          onCommit: (v) => v.trim() ? apply(czColHeadPath(col), v.trim(), 'Heading') : reset(czColHeadPath(col), 'Heading'),
        }}
        show={{ title: 'Show in the result grid', on: !czTruthy(s0.hidden), onChange: (on) => apply(`column_settings.${col}.hidden`, !on, on ? 'Show column' : 'Hide column') }}
        editable={{
          on: !czTruthy(s0.protect) && editableCapable && !!process.columnEditing,
          disabled: !editableCapable || !process.columnEditing,
          hint: !process.columnEditing || !editableCapable ? 'Not editable in this process' : 'Off makes it read-only for your organization',
          onChange: (on) => apply(`column_settings.${col}.protect`, !on, on ? 'Editable' : 'Read-only'),
        }}
        onResetAll={() => void czSession.resetUnder([`column_settings.${col}`, czColHeadPath(col)], 'Reset column')}
        resetAllDisabled={!czSession.countCustomized(`column_settings.${col}`) && !czSession.isCustomized(czColHeadPath(col))}
      />
    )
  })() : null

  return (
    <div data-tw-state={running ? 'loading' : preview ? (preview.row_count ? 'ready' : 'empty') : 'idle'}
         data-tw-rows={preview ? String(preview.row_count) : undefined}>
      {customizing && (
        <div style={{ margin: '-16px -16px 14px' }}>
          <CustomizeBar
            title={process.name || savedObjName || ''}
            session={czSession}
            views={[{ id: 'criteria', label: 'Criteria form' }, { id: 'grid', label: 'Result grid' }]}
            view={czView}
            onView={(id) => { setCzPanel(null); setCzView(id as 'criteria' | 'grid') }}
            onDone={() => { setCustomizing(false); setCzPanel(null) }}
            resetOptions={[
              czView === 'criteria'
                ? { label: 'Reset the criteria form', count: czSession.countCustomized('criteria_settings'), run: () => { void czSession.resetUnder(['criteria_settings'], 'Reset criteria') } }
                : { label: 'Reset the result grid', count: czSession.countCustomized('column_settings') + czSession.countCustomized('column_headers') + czSession.countCustomized('calculations'), run: () => { void czSession.resetUnder(['column_settings', 'column_headers', 'calculations'], 'Reset grid') } },
              { label: 'Reset everything on this process', count: czSession.countCustomized(), run: () => { void czSession.resetAll() } },
            ]}
          />
        </div>
      )}
      {czPanelNode}
      {fromFile ? (
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <SectionHeader>Import file</SectionHeader>
            <div style={{ flex: 1 }} />
            <input
              ref={fileInput}
              type="file"
              accept=".csv,.tsv,.txt,text/csv"
              style={{ display: 'none' }}
              onChange={(e) => void pickImportFile(e.target.files?.[0])}
            />
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => fileInput.current?.click()}
              {...tw('action', 'import_file')}
            >
              <UploadCloud size={13} /> {imported ? 'Choose another file' : 'Choose CSV file'}
            </button>
            {imported && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => { setImported(null); setPreview(null); setImportError('') }}
                title="Clear the imported file"
              >
                <X size={13} />
              </button>
            )}
          </div>

          {!imported && !importError && (
            <div style={emptyHintStyle}>
              This process runs on an imported file. Choose the CSV to process,
              then Refresh to review the calculated rows before executing.
            </div>
          )}
          {importError && (
            <div style={{ ...hintStyle, color: 'var(--color-danger, #d33)' }}>
              {importError}
            </div>
          )}
          {imported && (
            <div style={hintStyle}>
              <strong>{imported.filename}</strong> — {imported.rows.length.toLocaleString()} row(s)
              {imported.unmappedHeaders.length > 0 && (
                <> · ignoring {imported.unmappedHeaders.length} unmapped
                  column(s): {imported.unmappedHeaders.slice(0, 6).join(', ')}
                  {imported.unmappedHeaders.length > 6 ? '…' : ''}</>
              )}
              {imported.missingOptional.length > 0 && (
                <> · blank for {imported.missingOptional.join(', ')}</>
              )}
              {importBlocked && (
                <div style={{ color: 'var(--color-danger, #d33)', marginTop: 4 }}>
                  {importBlocked}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
      <div style={cardStyle}>
        <button
          type="button"
          onClick={() => setCriteriaCollapsed((c) => !c)}
          title={criteriaCollapsed ? 'Show criteria' : 'Hide criteria'}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, width: '100%',
            background: 'transparent', border: 'none', cursor: 'pointer',
            padding: 0, textAlign: 'left',
            marginBottom: criteriaCollapsed ? 0 : 2,
          }}
        >
          {criteriaCollapsed
            ? <ChevronRight size={14} style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }} />
            : <ChevronDown size={14} style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }} />}
          <span style={{
            fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5,
            color: 'var(--color-text-secondary)', fontWeight: 600, flexShrink: 0,
          }}>
            Criteria
          </span>
          {criteriaCollapsed && criteriaSummary && (
            <span style={{
              fontSize: 13, color: 'var(--color-text-secondary)', opacity: 0.85,
              minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              · {criteriaSummary}
            </span>
          )}
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 13, color: 'var(--color-accent)', flexShrink: 0 }}>
            {criteriaCollapsed ? 'Edit' : 'Hide'}
          </span>
        </button>
        {customizing && czView === 'criteria' ? czCriteria : !criteriaCollapsed && (
          <CriteriaForm
            dense
            sourceSql={process.source_sql}
            values={criteriaValues}
            onChange={onCriteriaChange}
            onCriteriaLabels={setCriteriaLabels}
            labels={process.criteria_labels}
            lookups={process.criteria_lookups}
            fetchLookup={fetchCriterionLookup}
            criteriaSettings={process.criteria_settings}
          />
        )}
      </div>
      )}

      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <SectionHeader>Result</SectionHeader>
          <div style={{ flex: 1 }} />
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            style={{ ...selectStyle, width: 110 }}
          >
            <option value={10}>First 10</option>
            <option value={50}>First 50</option>
            <option value={200}>First 200</option>
            <option value={1000}>First 1000</option>
            <option value={0}>All rows</option>
          </select>
          {canEditCalcs && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => openEditor(null)}
              title="Add a calculated column"
            >
              <Plus size={14} /> Add calculation
            </button>
          )}
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => runPreview()}
            {...tw('action', 'preview')}
            disabled={running !== null || awaitingImport}
            title={awaitingImport
              ? 'Import a CSV file first'
              : 'Run the process and show the calculated rows'}
          >
            {running === 'preview'
              ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              : <RefreshCw size={14} />}
            {running === 'preview' ? 'Running…' : 'Refresh data'}
          </button>
          {/* Deploying this process's business logic is an authoring action,
              and the backend keeps it on LIVE_PREVIEW — so in the Run view
              (no `onPatch`, i.e. read-only design) don't offer a button that
              can only 403 for a runtime guest. */}
          {hasBL && onPatch && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={deployLogic}
              disabled={running !== null}
              title="Compile/deploy this process's business-logic functions (process, item_change, validation)"
            >
              {running === 'deploy'
                ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                : <UploadCloud size={14} />}
              Deploy logic
            </button>
          )}
          {(process.validations || []).length > 0 && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={runValidate}
              {...tw('action', 'validate')}
              disabled={running !== null}
              title="Validate the result rows without writing"
            >
              {running === 'validate'
                ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                : <ShieldCheck size={14} />}
              {running === 'validate' ? 'Validating…' : 'Validate rows'}
            </button>
          )}
          {isProvider ? (
            <span style={{ ...hintStyle, marginTop: 0 }}>
              Line-source provider — this process previews rows for other forms
              to pull in; it is never executed.
            </span>
          ) : showExecute ? (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => runPersist()}
              {...tw('action', 'execute')}
              disabled={running !== null}
              title={isBlMode
                ? "Hand every row to this process's business logic"
                : execMode === 'direct'
                  ? (isTxnMode
                    ? 'Run the process and create transactions from every row (no preview)'
                    : 'Run the process and write every row into the target table (no preview)')
                  : isTxnMode
                    ? 'Create transactions from the result rows'
                    : 'Write the result rows into the target table'}
            >
              {running === 'persist'
                ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                : isTxnMode || isBlMode ? <Play size={14} /> : <Database size={14} />}
              {running === 'persist' ? 'Executing…' : 'Execute'}
            </button>
          ) : (
            <span style={{ ...hintStyle, marginTop: 0 }}>
              {awaitingImport
                ? (importBlocked || 'Import a CSV file to enable Execute.')
                : hasRows
                  ? (execMode === 'edit'
                    ? 'Edit or tick one or more rows to enable Execute.'
                    : 'Select one or more rows to enable Execute.')
                  : 'Refresh the data, then select rows to enable Execute.'}
            </span>
          )}
          {customize && savedObjName && !customizing && (
            <CustomizeMenu
              enabled={customize.enabled}
              subtitle="Arrange the criteria and the result grid for your organization"
              onCustomize={() => { setCzPanel(null); setCzView('criteria'); setCustomizing(true) }}
            />
          )}
        </div>

        {customizing ? (czView === 'grid' ? czGrid : (
          <div style={{ ...hintStyle, marginTop: 0 }}>Switch to “Result grid” above to arrange its columns.</div>
        )) : preview ? (
          <PreviewTable
            columns={shownColumns}
            rows={preview.rows}
            rowCount={preview.row_count}
            calcColumns={preview.calc_columns}
            traces={preview.traces}
            previousRows={previousPreview?.rows}
            columnHeaders={columnHeaders}
            enableSelection={!!process.MultiRowSelect}
            editableColumns={editableSpec}
            lockedColumns={lockedColumns}
            onSelectionChange={setSelectedRows}
            onRowsEdited={setEditedRows}
            recalcRow={recalc}
            fetchLookup={fetchLookup}
            columnWidths={process.column_widths}
            onColumnWidthsChange={(widths) => onPatch?.({ column_widths: widths })}
            onColumnHeaderChange={(field, header) => {
              const next = { ...(process.column_headers || {}) }
              if (header.trim()) next[field] = header.trim()
              else delete next[field]
              onPatch?.({ column_headers: next })
            }}
            savedFilters={process.filters}
            onSavedFiltersChange={(filters) => onPatch?.({ filters })}
            calcCount={canEditCalcs ? (process.calculations || []).length : undefined}
            calcIndexByColumn={canEditCalcs ? calcIndexByColumn : undefined}
            onEditCalc={canEditCalcs ? (col) => {
              const i = calcIndexByColumn(col)
              if (i >= 0) openEditor(i)
            } : undefined}
            onDeleteCalc={canEditCalcs ? (col) => {
              const i = calcIndexByColumn(col)
              if (i >= 0) void onDeleteCalc(i)
            } : undefined}
            onMoveCalc={canEditCalcs ? (col, d) => {
              const i = calcIndexByColumn(col)
              if (i >= 0) void onMoveCalc(i, d)
            } : undefined}
            errorCells={errorCells}
            focusCell={focusCell}
          />
        ) : (
          <div style={emptyHintStyle}>
            Click <strong>Refresh data</strong> to run the source SQL +
            calculations without writing anything.
          </div>
        )}
      </div>

      {report && (
        <ValidationReportPanel
          report={report}
          onClose={() => { setReport(null); setFocusCell(null) }}
          onTrace={(issue) => {
            if (issue.row == null) return
            setFocusCell({
              row: issue.row, col: issue.column, n: (focusCell?.n || 0) + 1,
            })
          }}
        />
      )}
      {warningsModal}

      {persistResult && (
        <div style={cardStyle}>
          <SectionHeader>Execution output</SectionHeader>
          {/* What the process's own business logic said it did — the most
              accurate account of the run, so it leads. */}
          {persistResult.message && (
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
              {persistResult.message}
            </div>
          )}
          {persistResult.mode === 'business_logic' ? (
            <div style={{ fontSize: 14 }}>
              Business logic{persistResult.method
                ? <> <code>{persistResult.method}</code></> : null}{' '}
              processed <strong>{persistResult.rows ?? 0}</strong> row(s)
              {persistResult.target_database && persistResult.target_database !== 'project database'
                ? <> on <code>{persistResult.target_database}</code></> : null}.
            </div>
          ) : persistResult.mode === 'transaction' ? (
            <>
              <div style={{ fontSize: 14 }}>
                Created <strong>{persistResult.transactions}</strong> transaction(s) of{' '}
                <code>{persistResult.target_object}</code> from{' '}
                <strong>{persistResult.rows}</strong> row(s).
              </div>
              {(persistResult.groups || []).length > 0 && (
                <ul style={{ margin: '6px 0 0 18px', fontSize: 13.5 }}>
                  {persistResult.groups!.slice(0, 50).map((g, i) => (
                    <li key={i}>
                      <code>{g.group}</code> → record <strong>{String(g.txn_id ?? '?')}</strong>
                      {g.detail_rows && Object.keys(g.detail_rows).length > 0 && (
                        <span style={{ color: 'var(--color-text-secondary)' }}>
                          {' '}({Object.entries(g.detail_rows)
                            .map(([f, n]) => `form ${f}: ${n} row${n === 1 ? '' : 's'}`)
                            .join(', ')})
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <div style={{ fontSize: 14 }}>
              Inserted <strong>{persistResult.inserted ?? 0}</strong>
              {(persistResult.updated ?? 0) > 0 && <> , updated <strong>{persistResult.updated}</strong></>}
              {' '}row(s) into <code>{persistResult.target_table}</code>
              {persistResult.table_created ? ' (table just created)' : ''}
              {persistResult.target_database && persistResult.target_database !== 'project database'
                ? <> on <code>{persistResult.target_database}</code></> : null}.
            </div>
          )}
          {(persistResult.warnings || []).length > 0 && (
            <div style={{ ...hintStyle, color: 'var(--color-warning, #f5a524)' }}>
              {persistResult.warnings!.map((w, i) => <div key={i}>⚠ {w}</div>)}
            </div>
          )}
        </div>
      )}

      {editorOpen && (
        <CalculationEditDialog
          open
          calc={editingIndex == null ? null : (process.calculations || [])[editingIndex] || null}
          availableColumns={availableColumns}
          onCancel={() => { setEditorOpen(false); setEditingIndex(null) }}
          onSave={onSaveCalc}
        />
      )}
    </div>
  )
}

/** How one issue is labelled — "Row 4 · qty" for a row it points at, "Process"
    for a whole-run issue (the process function saw the entire set). Shared by
    the report panel and the warnings dialog so both name the same thing. */
function issueLabel(issue: ValidationIssue | undefined, message: string): string {
  if (!issue) return message
  const where = issue.row == null
    ? 'Process'
    : `Row ${issue.row + 1}${issue.column ? ` · ${issue.column}` : ''}`
  return `${where} — ${message}`
}

/** Row+column-traceable validation results. Clicking an issue scrolls the grid
    to that cell and flashes it.

    Warnings are LISTED here but not acknowledged here: proceeding past them is
    the shared transaction dialog's job (it appears the moment they are raised),
    so this panel is what the user reads while CORRECTING the data. */
function ValidationReportPanel({
  report, onClose, onTrace,
}: {
  report: {
    code: string; errors: ValidationIssue[]; warnings: ValidationIssue[]
    prompts: ValidationIssue[]; savedGroups?: number
  }
  onClose: () => void
  onTrace: (issue: ValidationIssue) => void
}) {
  const { errors, warnings, prompts } = report
  if (!errors.length && !warnings.length && !prompts.length) {
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--color-success, #16a34a)' }}>
          <CheckCircle2 size={16} /> All rows valid — nothing blocks Execute.
          <div style={{ flex: 1 }} />
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={14} /></button>
        </div>
      </div>
    )
  }
  const issueList = (items: ValidationIssue[], color: string) => (
    <ul style={{ margin: '4px 0 0 0', padding: 0, listStyle: 'none' }}>
      {items.slice(0, 200).map((i, k) => (
        <li key={k}
          onClick={() => onTrace(i)}
          title={i.row == null
            ? 'Reported about the run as a whole'
            : 'Click to find this cell on the result grid'}
          style={{
            cursor: i.row == null ? 'default' : 'pointer',
            fontSize: 13.5, padding: '3px 6px', borderRadius: 4,
            borderLeft: `3px solid ${color}`, marginBottom: 3,
            background: 'var(--color-bg-secondary)',
          }}>
          <strong>{i.row == null ? 'Process' : `Row ${i.row + 1}`}</strong>
          {i.column ? <> · <code>{i.column}</code></> : null}: {i.message}
          {i.code ? <span style={{ ...hintStyle, marginLeft: 4 }}>[{i.code}]</span> : null}
        </li>
      ))}
      {items.length > 200 && (
        <li style={{ ...hintStyle, marginTop: 2 }}>…and {items.length - 200} more</li>
      )}
    </ul>
  )
  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <SectionHeader>
          {errors.length ? 'Validation failed' : warnings.length ? 'Validation warnings' : 'Notices'}
        </SectionHeader>
        <div style={{ flex: 1 }} />
        <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={14} /></button>
      </div>

      {errors.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, fontWeight: 600, color: '#ef4444' }}>
            <AlertTriangle size={14} /> {errors.length} error(s) — fix these before Execute
          </div>
          {issueList(errors, '#ef4444')}
        </div>
      )}

      {warnings.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, fontWeight: 600, color: '#f5a524' }}>
            <AlertTriangle size={14} /> {warnings.length} warning(s)
          </div>
          {issueList(warnings, '#f5a524')}
        </div>
      )}

      {prompts.length > 0 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, fontWeight: 600, color: '#3b82f6' }}>
            <Info size={14} /> {prompts.length} note(s)
          </div>
          {issueList(prompts, '#3b82f6')}
        </div>
      )}

      {report.savedGroups ? (
        <div style={{ ...hintStyle, color: '#f5a524' }}>
          {report.savedGroups} transaction(s) were already saved before this one
          was refused — re-running will not create them twice only if the
          process is keyed for upsert.
        </div>
      ) : null}
      <div style={hintStyle}>
        Click any issue to scroll to and highlight the offending cell on the
        result grid above. Fix the data, then Execute again.
      </div>
    </div>
  )
}

/** Workbench "Live Preview" pane — loads the SAVED process and runs it. Layout
    and calc edits made here auto-save back to the design JSON (debounced).

    The SAME pane backs the end-user "Run" surface (and is the basis for the
    downloaded-app runtime), so preview / Run / downloaded app stay uniform. In
    `runMode` the design is read-only: layout/calc edits are NOT offered and
    nothing is auto-saved back to the model — the end-user only supplies
    criteria, previews, edits result rows and persists. */
export function GenProcessLivePreviewPane({
  project, objName, runMode = false, customizable = false, initialCriteria, protectedCriteria,
}: {
  project: string; objName: string; runMode?: boolean
  /** Offer the ⋮ "Customize" entry (Run view) to a caller who may customize. */
  customizable?: boolean
  /** Criteria values to start from — an 'open' action button on a
   *  transaction passing values from the row it was clicked on. They win over
   *  the process's own defaults. */
  initialCriteria?: Record<string, unknown>
  /** Of those, the criteria shown locked (presentation only: the person could
   *  run this process with any value from its own menu entry). */
  protectedCriteria?: string[]
}) {
  // The passed values, applied over the process defaults on every (re)load.
  const initialRef = useRef(initialCriteria)
  initialRef.current = initialCriteria
  const startValues = useCallback((p: GenProcess) => ({
    ...(p.default_params || {}), ...(initialRef.current || {}),
  }), [])
  const [process, setProcess] = useState<GenProcess | null>(null)
  const [canCustomize, setCanCustomize] = useState(false)
  useEffect(() => {
    if (!customizable) { setCanCustomize(false); return undefined }
    let cancelled = false
    modelOverlayCanCustomize(project, 'T').then((v) => { if (!cancelled) setCanCustomize(v) })
    return () => { cancelled = true }
  }, [project, customizable])
  const customize = useMemo(() => (customizable ? {
    transport: {
      get: (t, n) => modelOverlayGet(project, n, t),
      patch: (t, n, set, reset) => modelOverlayPatch(project, n, set, reset, t),
      reset: (t, n) => modelOverlayReset(project, n, t),
    } as CustomizeTransport,
    enabled: canCustomize,
    // Every saved change: re-render with the process as this organization
    // now sees it, in place (the run panel stays in Customize mode).
    onEffective: (res: ModelOverlayResult) => {
      if (res?.effective && Object.keys(res.effective as object).length) {
        const p = sanitizeProcess(res.effective, objName)
        setProcess(p)
        setCriteriaValues(startValues(p))
      }
    },
  } : undefined), [customizable, canCustomize, project, objName, startValues])
  const [criteriaValues, setCriteriaValues] = useState<Record<string, unknown>>({})
  const [loading, setLoading] = useState(true)
  const agentRefresh = useAgentRefresh(project, false)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pending = useRef<GenProcess | null>(null)

  setGenprocCtx({ project })

  // Load on mount + whenever the AI agent touches the project's files.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    // The Run view reads the process as THIS organization sees it — the
    // shared design plus its functional administrator's customization
    // (criteria labels / order / hidden / read-only / defaults, grid
    // columns). The designer's own preview edits the design itself.
    const load = runMode
      ? modelOverlayGet(project, objName, 'G')
          .then((r) => ({ content: r.effective && Object.keys(r.effective).length ? r.effective : null }))
          .catch(() => loadProcessObject(project, objName))
      : loadProcessObject(project, objName)
    load
      .then(({ content }) => {
        if (cancelled) return
        const p = sanitizeProcess(content, objName)
        setProcess(p)
        setCriteriaValues(startValues(p))
      })
      .catch(() => { if (!cancelled) setProcess(null) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [project, objName, agentRefresh, runMode, startValues])

  // A locked passed criterion is drawn read-only carrying its value — the
  // criteria form's own "read-only criterion" path, applied to this screen
  // only (the process design is not touched).
  const lockKey = (protectedCriteria || []).join('|')
  const shownProcess = useMemo(() => {
    if (!process || !protectedCriteria?.length) return process
    const settings = { ...(process.criteria_settings || {}) }
    for (const name of protectedCriteria) {
      const key = Object.keys(settings).find(k => k.toLowerCase() === name.toLowerCase()) || name
      const value = Object.entries(initialRef.current || {})
        .find(([k]) => k.toLowerCase() === name.toLowerCase())?.[1]
      settings[key] = { ...(settings[key] || {}), protect: true, ...(value !== undefined ? { default: value } : {}) }
    }
    return { ...process, criteria_settings: settings }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [process, lockKey])

  // Clean up a pending debounced save on unmount.
  useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current) }, [])

  // Auto-save layout/calc edits back to the design (debounced).
  const onPatch = useCallback((patch: Partial<GenProcess>) => {
    setProcess((prev) => {
      if (!prev) return prev
      const next = { ...prev, ...patch }
      pending.current = next
      if (saveTimer.current) clearTimeout(saveTimer.current)
      saveTimer.current = setTimeout(() => {
        const p = pending.current
        if (p) {
          saveProcessObject(project, objName, p).catch(() => {
            toast.error('Could not save layout to the design')
          })
        }
      }, 700)
      return next
    })
  }, [project, objName])

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: '100%' }}>
        <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: 8 }}>Loading process…</span>
      </div>
    )
  }
  if (!process) {
    return (
      <div style={{ ...emptyHintStyle, padding: 24 }}>
        No saved process design yet — open the Design tab and save one first.
      </div>
    )
  }
  return (
    <div {...tw('screen', objName, { kind: 'G' })} style={{ padding: 16, overflowY: 'auto', height: '100%' }}>
      <GenprocMuiProvider>
        <RunPanel
          project={project}
          process={shownProcess || process}
          criteriaValues={criteriaValues}
          onCriteriaChange={setCriteriaValues}
          onPatch={runMode ? undefined : onPatch}
          savedObjName={runMode ? objName : undefined}
          customize={runMode ? customize : undefined}
        />
      </GenprocMuiProvider>
    </div>
  )
}
